import { Fragment, useEffect, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  AccountsAndLocations,
  PickerAccount,
  PickerLocation,
  fetchAccountsAndLocations,
} from "./appUpdatesApi";
import { PrimaryButton, SecondaryButton } from "../../components/Button";

interface Props {
  open: boolean;
  serialNumber: string | null;
  currentLocationId?: string | null;
  // Where the reader currently lives, so we can disable that option in the
  // dropdown and surface "(current)" alongside it.
  currentAccountId?: string | null; // null/empty = platform
  onClose: () => void;
  onConfirm: (input: {
    destination_account_id: string; // "" = platform
    destination_location_id: string;
    account_name: string;
    location_name: string;
  }) => Promise<void>;
}

// Internal sentinel for the platform account so it doesn't collide with the
// "Select an account…" placeholder option (which has value=""). Converted
// back to "" at the API boundary.
const PLATFORM_KEY = "__platform__";

const accountKey = (a: PickerAccount, isPlatform: boolean) =>
  isPlatform ? PLATFORM_KEY : a.id;

const apiAccountId = (key: string) => (key === PLATFORM_KEY ? "" : key);

export const MoveDestinationModal = ({
  open,
  serialNumber,
  currentLocationId,
  currentAccountId,
  onClose,
  onConfirm,
}: Props) => {
  const [data, setData] = useState<AccountsAndLocations | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeOnly, setActiveOnly] = useState(true);
  const [selectedAccountKey, setSelectedAccountKey] = useState<string>("");
  const [selectedLocationId, setSelectedLocationId] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  // Lazy-fetch directory the first time the modal opens.
  useEffect(() => {
    if (!open || data) return;
    setLoading(true);
    setError(null);
    fetchAccountsAndLocations()
      .then((d) => setData(d))
      .catch((e: any) => setError(e?.message ?? "Failed to load accounts"))
      .finally(() => setLoading(false));
  }, [open, data]);

  // Reset selection when the modal closes.
  useEffect(() => {
    if (!open) {
      setSelectedAccountKey("");
      setSelectedLocationId("");
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const accountOptions = useMemo(() => {
    if (!data) return [] as { key: string; account: PickerAccount; isPlatform: boolean }[];
    const out: { key: string; account: PickerAccount; isPlatform: boolean }[] = [
      { key: PLATFORM_KEY, account: data.platform, isPlatform: true },
    ];
    for (const a of data.connected) {
      if (activeOnly && !a.in_payment_processor_config) continue;
      out.push({ key: a.id, account: a, isPlatform: false });
    }
    return out;
  }, [data, activeOnly]);

  const selectedAccount = useMemo(() => {
    return accountOptions.find((o) => o.key === selectedAccountKey)?.account ?? null;
  }, [accountOptions, selectedAccountKey]);

  const selectedLocation = useMemo(
    () =>
      selectedAccount?.locations.find((l) => l.id === selectedLocationId) ??
      null,
    [selectedAccount, selectedLocationId]
  );

  // Note: currentAccountId comes from the parent as the API-style value
  // (""=platform). Translate to internal-key space for comparison.
  // Platform-hosted locations (the standard setup) physically live on the
  // platform account even when listed under a merchant, so compare against
  // the platform key for those.
  const currentAccountKey = currentAccountId ? currentAccountId : PLATFORM_KEY;
  const isCurrentLocation = (loc: PickerLocation) => {
    const effectiveAccountKey = loc.on_platform
      ? PLATFORM_KEY
      : selectedAccountKey;
    return (
      effectiveAccountKey === currentAccountKey && loc.id === currentLocationId
    );
  };

  // Spelled out under the picker: a <select> option is easy to skim past, and
  // this is the difference between the reader charging through Ronin or
  // through the merchant.
  const placementNote = selectedLocation
    ? selectedLocation.on_platform
      ? "This location is on RONIN's account. The reader will be registered there, its taps will charge Ronin, and it needs a deploy group entry picked from Ronin's own location list."
      : "This location is on the MERCHANT's own account. The reader will be registered there and its taps will charge the merchant directly."
    : null;

  const canConfirm =
    !!selectedAccount &&
    !!selectedLocation &&
    !isCurrentLocation(selectedLocation) &&
    !submitting;

  const handleConfirm = async () => {
    if (!selectedAccount || !selectedLocation) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm({
        // Platform-hosted locations must register the reader on the platform
        // account regardless of which merchant was picked — registering a
        // platform tml_… id on a connected account gets rejected by Stripe.
        destination_account_id: selectedLocation.on_platform
          ? ""
          : apiAccountId(selectedAccountKey),
        destination_location_id: selectedLocation.id,
        account_name: selectedAccount.name,
        location_name: selectedLocation.name,
      });
      onClose();
    } catch (e: any) {
      setError(e?.message ?? "Move failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-4"
            >
              <Dialog.Panel className="relative w-full max-w-lg bg-white rounded-xl shadow-xl">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                  <div>
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Move reader
                    </Dialog.Title>
                    {serialNumber && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        SN: <span className="font-mono">{serialNumber}</span>
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                <div className="px-6 py-4 space-y-4">
                  {loading && (
                    <div className="text-sm text-gray-500">Loading accounts and locations…</div>
                  )}
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                      {error}
                    </div>
                  )}

                  {data && !loading && (
                    <>
                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={activeOnly}
                          onChange={(e) => {
                            setActiveOnly(e.target.checked);
                            // If currently-selected account gets hidden, reset.
                            setSelectedAccountKey("");
                            setSelectedLocationId("");
                          }}
                          className="rounded border-gray-300"
                        />
                        Show only active accounts (in payment_processor_config)
                      </label>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Destination account
                        </label>
                        <select
                          value={selectedAccountKey}
                          onChange={(e) => {
                            setSelectedAccountKey(e.target.value);
                            setSelectedLocationId("");
                          }}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        >
                          <option value="" disabled hidden>
                            Select an account…
                          </option>
                          {accountOptions.map((opt) => {
                            const isCurrent = opt.key === currentAccountKey;
                            return (
                              <option key={opt.key} value={opt.key}>
                                {/* Our name for the venue leads. The legal
                                    business name Stripe holds is appended only
                                    when it differs, because that is the name
                                    ops has to search for in the Stripe
                                    dashboard when adding a deploy group. */}
                                {opt.account.name}
                                {!opt.isPlatform &&
                                opt.account.stripe_business_name &&
                                opt.account.stripe_business_name !== opt.account.name
                                  ? ` · Stripe: ${opt.account.stripe_business_name}`
                                  : ""}
                                {opt.isPlatform ? " (platform)" : ""}
                                {isCurrent ? " · current" : ""}
                                {!opt.isPlatform && !opt.account.in_payment_processor_config
                                  ? " · not in config"
                                  : ""}
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Destination location
                        </label>
                        <select
                          value={selectedLocationId}
                          onChange={(e) => setSelectedLocationId(e.target.value)}
                          disabled={!selectedAccount}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:opacity-60"
                        >
                          <option value="" disabled hidden>
                            {selectedAccount ? "Select a location…" : "(pick an account first)"}
                          </option>
                          {selectedAccount?.locations.map((loc) => {
                            const isCurrent = isCurrentLocation(loc);
                            return (
                              <option key={loc.id} value={loc.id} disabled={isCurrent}>
                                {loc.name}
                                {loc.address ? ` · ${loc.address}` : ""}
                                {/* WHICH ACCOUNT this location physically lives
                                    on. Platform-hosted locations are listed
                                    under the merchant they belong to, so the
                                    name and address alone cannot tell them
                                    apart — and the two mean very different
                                    things for where the money goes and which
                                    deploy group covers the reader. */}
                                {loc.on_platform
                                  ? " · on RONIN's account"
                                  : " · on the merchant's account"}
                                {isCurrent ? " · current (disabled)" : ""}
                              </option>
                            );
                          })}
                        </select>
                        {placementNote && (
                          <p
                            className={`text-xs mt-1 rounded border px-2 py-1 ${
                              selectedLocation?.on_platform
                                ? "border-indigo-200 bg-indigo-50 text-indigo-800"
                                : "border-gray-200 bg-gray-50 text-gray-700"
                            }`}
                          >
                            {placementNote}
                          </p>
                        )}
                        {selectedAccount && selectedAccount.locations.length === 0 && (
                          <p className="text-xs text-amber-700 mt-1">
                            No locations for this account — set
                            platform_account_location_id in its
                            payment_processor_config, or create one via the
                            Stripe Connect page.
                          </p>
                        )}
                        {selectedAccount?.error && (
                          <p className="text-xs text-red-600 mt-1">
                            Couldn&apos;t list this account&apos;s own
                            locations: {selectedAccount.error}
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </div>

                <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
                  <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                  <PrimaryButton onClick={handleConfirm} isDisabled={!canConfirm}>
                    {submitting ? "Moving…" : "Move reader"}
                  </PrimaryButton>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
